/* ====================================================================================================================
 * FAYARD GIFT ORDER — « Votre commande est un cadeau ? »
 * --------------------------------------------------------------------------------------------------------------------
 * Élément personnalisé <fayard-gift-order>, rendu par snippets/fayard-gift-order.liquid dans le cart drawer.
 *
 * Pourquoi un custom element plutôt que des addEventListener globaux : Prestige re-rend intégralement le cart drawer
 * à chaque changement de panier (js/sections/cart-drawer.js -> replaceContent appelle replaceChildren). Chaque rendu
 * crée donc une nouvelle instance, dont connectedCallback attache ses propres écouteurs, et l'ancienne est détruite
 * avec les siens. Aucun risque d'écouteurs dupliqués.
 *
 * L'état vit dans les attributs de panier, pas dans le DOM, pour survivre aux re-rendus, à la fermeture du drawer
 * et au rechargement de page. Le sac cadeau, lui, est une vraie ligne de panier : il est lu depuis cart.items côté
 * Liquid, ce qui rend un double ajout structurellement impossible (le bouton « + » disparaît dès qu'il est présent).
 *
 * Le sac cadeau n'utilise aucun code ici : l'ajout passe par <product-form> et les quantités par
 * <line-item-quantity>, deux composants natifs du thème.
 * ==================================================================================================================== */

(function () {
  'use strict';

  /* Attributs visibles par le marchand dans l'admin Shopify (section « Informations supplémentaires » de la commande). */
  var ATTRIBUTE_GIFT = 'Commande cadeau';
  var ATTRIBUTE_MESSAGE = 'Message cadeau';
  var ATTRIBUTE_SIGNATURE = 'Signature cadeau';

  /* Brouillons masqués : un attribut préfixé d'un underscore n'apparaît ni au checkout ni dans l'admin. Ils conservent
     ce que le client a écrit même s'il décoche la case, sans polluer une commande qui n'est finalement pas un cadeau. */
  var ATTRIBUTE_MESSAGE_DRAFT = '_gift_message_draft';
  var ATTRIBUTE_SIGNATURE_DRAFT = '_gift_signature_draft';

  var SAVE_DELAY = 700;

  /* Dernier état connu, au niveau du module donc conservé d'un rendu à l'autre. Il couvre la fenêtre pendant laquelle
     le panier a déjà renvoyé son HTML alors que l'enregistrement des attributs n'est pas encore arrivé au serveur. */
  var lastKnownState = { gift: null, message: null, signature: null };

  var FayardGiftOrder = class extends HTMLElement {
    connectedCallback() {
      this.toggleInput = this.querySelector('[data-gift-toggle]');
      this.panel = this.querySelector('[data-gift-panel]');
      this.messageInput = this.querySelector('[data-gift-message]');
      this.signatureInput = this.querySelector('[data-gift-signature]');
      this.counterValue = this.querySelector('[data-gift-counter-value]');
      this.counter = this.querySelector('[data-gift-counter]');
      this.saveTimer = null;
      this.isDirty = false;

      this.restoreState();
      this.updateCounter();

      if (this.toggleInput) {
        this.toggleInput.addEventListener('change', this.onToggle.bind(this));
      }

      [this.messageInput, this.signatureInput].forEach(
        function (field) {
          if (!field) return;
          field.addEventListener('input', this.onInput.bind(this));
          field.addEventListener('change', this.save.bind(this));
        }.bind(this)
      );
    }

    disconnectedCallback() {
      /* Un re-rendu du drawer peut détruire l'élément alors qu'une saisie n'est pas encore partie : on la pousse
         avant de disparaître. keepalive garantit que la requête aboutit même si le document change. */
      window.clearTimeout(this.saveTimer);
      if (this.isDirty) this.save();
    }

    get isGift() {
      return !!(this.toggleInput && this.toggleInput.checked);
    }

    /**
     * Réapplique ce que le client avait saisi si le HTML rendu par le serveur est en retard sur l'état local.
     */
    restoreState() {
      if (lastKnownState.gift !== null && this.toggleInput) {
        this.toggleInput.checked = lastKnownState.gift;
      }

      if (lastKnownState.message !== null && this.messageInput) {
        this.messageInput.value = lastKnownState.message;
      }

      if (lastKnownState.signature !== null && this.signatureInput) {
        this.signatureInput.value = lastKnownState.signature;
      }

      this.syncPanel();
    }

    syncPanel() {
      if (!this.panel) return;
      this.panel.hidden = !this.isGift;
      if (this.toggleInput) {
        this.toggleInput.setAttribute('aria-expanded', this.isGift ? 'true' : 'false');
      }
    }

    onToggle() {
      this.syncPanel();
      this.save();

      if (this.isGift && this.messageInput) {
        this.messageInput.focus({ preventScroll: true });
      }
    }

    onInput() {
      this.isDirty = true;
      this.updateCounter();
      this.rememberState();
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(this.save.bind(this), SAVE_DELAY);
    }

    updateCounter() {
      if (!this.counterValue || !this.messageInput) return;

      var length = this.messageInput.value.length;
      var max = parseInt(this.messageInput.getAttribute('maxlength'), 10);

      this.counterValue.textContent = length;

      if (this.counter) {
        if (!isNaN(max) && length >= max) {
          this.counter.setAttribute('data-gift-counter-full', '');
        } else {
          this.counter.removeAttribute('data-gift-counter-full');
        }
      }
    }

    rememberState() {
      lastKnownState.gift = this.isGift;
      if (this.messageInput) lastKnownState.message = this.messageInput.value;
      if (this.signatureInput) lastKnownState.signature = this.signatureInput.value;
    }

    /**
     * Enregistre l'état dans les attributs de panier. Même mécanisme que <cart-note> et <collection-layout-switch>
     * du thème : un POST sur cart/update.js, en keepalive pour qu'il aboutisse même si la page navigue juste après.
     */
    save() {
      window.clearTimeout(this.saveTimer);
      this.isDirty = false;
      this.rememberState();

      var isGift = this.isGift;
      var attributes = {};

      attributes[ATTRIBUTE_GIFT] = isGift ? this.getAttribute('gift-value') || 'Oui' : '';

      if (this.messageInput) {
        var message = this.messageInput.value;
        attributes[ATTRIBUTE_MESSAGE_DRAFT] = message;
        attributes[ATTRIBUTE_MESSAGE] = isGift ? message : '';
      }

      if (this.signatureInput) {
        var signature = this.signatureInput.value;
        attributes[ATTRIBUTE_SIGNATURE_DRAFT] = signature;
        attributes[ATTRIBUTE_SIGNATURE] = isGift ? signature : '';
      }

      fetch(Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        keepalive: true
      });
    }
  };

  if (!window.customElements.get('fayard-gift-order')) {
    window.customElements.define('fayard-gift-order', FayardGiftOrder);
  }
})();
